/**
 * The attribute a run lays on the revision elements (`w:ins`, `w:del`, `w:moveTo`,
 * `w:moveFrom`) of the changes its reverts name, holding the names of those reverts.
 *
 * WHY an attribute on the change's own elements
 *   The change a revert names is found once, on the page as first read, and has to be
 *   found again after other cards wrote in the paragraph. Its id moved, and words or a
 *   place among neighbours are not the change. Our writes never take an attribute off
 *   another author's element, and when the splicer cuts an element in two it clones it
 *   (`cloneNode(false)` in `ParagraphSplicer`), so both halves carry the names.
 *
 * WHY it never reaches Word
 *   Word was never asked to keep an attribute it does not know inside a package it
 *   inserts: it might drop it or refuse the write. Every run that lays a mark strips it
 *   before the package leaves the run (`RevertTargetMarks`).
 *
 * EXAMPLE
 *   <w:ins w:id="281" w:author="DXC Legal" xmlns:dioptraRevertTarget="urn:dioptra:revert-target"
 *          dioptraRevertTarget:names="%5B%22_general_package_cookie%22%2C%22c2%22…">
 */
import { DELETION_CONTAINERS, elementNameWithoutPrefix, INSERTION_CONTAINERS } from "./ooxml-names";

/** The namespace of the mark: private to the run, and the string a package is searched for to tell it carries one. */
export const REVERT_TARGET_MARK_NAMESPACE = "urn:dioptra:revert-target";
const REVERT_TARGET_MARK_PREFIX = "dioptraRevertTarget";
const REVERT_TARGET_MARK_LOCAL_NAME = "names";
const XMLNS_NAMESPACE = "http://www.w3.org/2000/xmlns/";

/**
 * The revert names a revision element carries, in the order they were laid.
 *
 * EXAMPLE
 *   names="a b" -> ["a", "b"]; no attribute -> []
 */
export function revertTargetNamesOn(revisionElement: Element): string[] {
    const names = revisionElement.getAttributeNS(REVERT_TARGET_MARK_NAMESPACE, REVERT_TARGET_MARK_LOCAL_NAME);
    /** NULL, or "": the element carries no mark. */
    if (names === null || names === "") return [];
    return names.split(" ");
}

/** Adds one revert's name to a revision element's mark, once. */
export function addRevertTargetName(revisionElement: Element, revertName: string): void {
    const names = revertTargetNamesOn(revisionElement);
    /** WHEN the element already carries the name CONSEQUENCE nothing to add. */
    if (names.includes(revertName)) return;
    revisionElement.setAttributeNS(REVERT_TARGET_MARK_NAMESPACE, `${REVERT_TARGET_MARK_PREFIX}:${REVERT_TARGET_MARK_LOCAL_NAME}`, [...names, revertName].join(" "));
}

/**
 * Takes revert names off every revision element under `root` (itself included), and the
 * mark with its namespace declaration off an element left with none.
 *
 * CASES
 *   - `revertNamesToTake` given -> those names only: a build strips the names it laid, and a
 *                                  run's names laid before the build stay for the cards after it
 *   - null                      -> every name, and the namespace's declaration off every element,
 *                                  wherever a serializer put it: the run is over
 */
export function takeRevertTargetNames(root: Element, revertNamesToTake: ReadonlySet<string> | null): void {
    for (const element of [root, ...Array.from(root.getElementsByTagName("*"))]) {
        const tag = elementNameWithoutPrefix(element);
        const names = INSERTION_CONTAINERS.has(tag) || DELETION_CONTAINERS.has(tag) ? revertTargetNamesOn(element) : [];
        const namesKept = revertNamesToTake === null ? [] : names.filter((revertName) => !revertNamesToTake.has(revertName));
        /**
         * CASES
         *   - some of the element's names stay -> the mark is rewritten with them alone
         *   - none stay, and it carried some   -> the mark and its declaration go
         *   - it carried none                  -> only the run's end takes a declaration off it
         */
        if (namesKept.length > 0) {
            element.setAttributeNS(REVERT_TARGET_MARK_NAMESPACE, `${REVERT_TARGET_MARK_PREFIX}:${REVERT_TARGET_MARK_LOCAL_NAME}`, namesKept.join(" "));
            continue;
        }
        if (names.length > 0) element.removeAttributeNS(REVERT_TARGET_MARK_NAMESPACE, REVERT_TARGET_MARK_LOCAL_NAME);
        if (names.length > 0 || revertNamesToTake === null) removeMarkNamespaceDeclarations(element);
    }
}

/**
 * Takes every declaration of the mark's namespace off one element, whatever prefix it was
 * written under.
 *
 * WHY by the namespace and not the prefix
 *   A serializer that meets the mark's attribute again after a parse may declare the namespace
 *   under a prefix of its own (`xmlns:ns1="urn:dioptra:revert-target"`), so a declaration taken
 *   off by the prefix the mark was laid with stayed in the package handed Word.
 */
function removeMarkNamespaceDeclarations(element: Element): void {
    const declarations = Array.from(element.attributes).filter((attribute) => attribute.namespaceURI === XMLNS_NAMESPACE && attribute.value === REVERT_TARGET_MARK_NAMESPACE);
    for (const declaration of declarations) element.removeAttributeNode(declaration);
}
